import React from 'react';

import cn from 'clsx';

import { Body2, Heading1, Heading2, Heading4, Heading5, InformationTooltip, useDeviceType } from '@cian/ui-kit';
import { IconFinanceFamily24 } from '@cian/ui-kit-design-tokens/icons';
import { ETestIds } from 'shared/constants/testIds';
import { EBenefitProgram } from 'shared/repositories/mortgage-broker-calculator/entities/get_settings/ProgramsSummary';

import { familyLoanAmountHint, IProgramCardTermHint } from './familyLoanAmountHint';
import { getProgramCardDetails } from './programCardDetails';
import styles from './styles.css';

export interface IProgramCardProps {
  title: string;
  icon?: React.ReactNode;
  benefitProgram?: EBenefitProgram;
  rate: string;
  loanTerm: string;
  loanAmount: string;
  downPayment: string;
  details?: React.ReactNode;
}

interface IProgramCardTermProps {
  label: string;
  value: string;
  isDesktop: boolean;
  hint?: IProgramCardTermHint;
}

const ProgramCardTerm: React.FC<IProgramCardTermProps> = ({ label, value, isDesktop, hint }) => {
  const TermValue = isDesktop ? Heading2 : Heading4;
  const deviceType = useDeviceType();

  const term = (
    <div className={cn(styles['term'], styles[deviceType], hint && styles['term-interactive'])}>
      <Body2>{label}</Body2>
      <div className={styles['term-value-block']}>
        <TermValue fontWeight="700">{value}</TermValue>
        {hint?.content}
      </div>
    </div>
  );

  if (!hint) {
    return term;
  }

  return (
    <InformationTooltip placement="bottom" content={hint.tooltip} portal>
      {term}
    </InformationTooltip>
  );
};

const getIconClassName = (benefitProgram?: EBenefitProgram) => {
  switch (benefitProgram) {
    case EBenefitProgram.Family:
      return styles['icon-family'];
    case EBenefitProgram.It:
      return styles['icon-it'];
    default:
      return styles['icon-default'];
  }
};

export const ProgramCard: React.FC<IProgramCardProps> = ({
  title,
  icon = <IconFinanceFamily24 color="icon-inverted-default" />,
  benefitProgram,
  rate,
  loanTerm,
  loanAmount,
  downPayment,
  details,
}) => {
  const deviceType = useDeviceType();
  const isDesktop = deviceType === 'desktop';
  const Title = isDesktop ? Heading1 : Heading2;
  const DetailsTitle = isDesktop ? Heading4 : Heading5;
  const resolvedDetails =
    details ?? (benefitProgram !== undefined ? getProgramCardDetails(benefitProgram, isDesktop) : undefined);

  return (
    <article
      className={cn(styles['root'], styles[deviceType])}
      data-testid={benefitProgram ? `${ETestIds.ProgramCard}-${benefitProgram}` : ETestIds.ProgramCard}
    >
      <header className={styles['header']}>
        <div className={cn(styles['icon'], getIconClassName(benefitProgram))}>{icon}</div>
        <Title fontWeight="700">{title}</Title>
      </header>

      <div className={cn(styles['terms'], styles[deviceType])}>
        <ProgramCardTerm isDesktop={isDesktop} label="Ставка" value={rate} />
        <ProgramCardTerm isDesktop={isDesktop} label="Срок кредита" value={loanTerm} />
        <ProgramCardTerm
          isDesktop={isDesktop}
          label="Сумма кредита"
          value={loanAmount}
          hint={benefitProgram === EBenefitProgram.Family ? familyLoanAmountHint : undefined}
        />
        <ProgramCardTerm isDesktop={isDesktop} label="Взнос" value={downPayment} />
      </div>

      {resolvedDetails && (
        <section className={cn(styles['details'], styles[deviceType])}>
          <div className={styles['details-title']}>
            <DetailsTitle fontWeight="700">Для кого</DetailsTitle>
          </div>
          {resolvedDetails}
        </section>
      )}
    </article>
  );
};
